import { textPaintPatch, syncPaintInput } from "./native-paint-input";
import { drawTextFrame } from "../render/native-text-frame";
import {
  originalCompact,
  syncOriginalCompact,
  wrapOriginalSection,
} from "./native-original-controls";
import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import type { SceneNode, Point } from "../scene/model";
import {
  documentPoint,
  drawBox,
  hitNode,
  hitFrameHandle,
  handles,
  type Camera,
} from "../native/geometry";
import {
  nodeMatrix,
  resizeMatrix,
  transformNativeNode,
} from "../native/transforms";
import { invertAffine, transformPoint } from "../geometry/affine";
import {
  createTextNode,
  textData,
  replaceText,
  styleText,
  characterStyle,
  textFont,
  type CharacterStyle,
} from "../native/text";
import { measureTextNode, textLayout } from "../render/native-text-layout";
import { curvedText, textPose, posePoint } from "../native/text-deformation";
import { textProjector } from "../render/native-text-projection";
import { NativeTextDeformation } from "./native-text-deformation";
import type { WorkTool } from "./native-tool-groups";
export class NativeTextEditor {
  tool: "text" | "text-field" | null = null;
  private id: string | null = null;
  private fresh = false;
  private touchSequence = false;
  private touchFocusPending = false;
  private loading = 0;
  private input = document.createElement("textarea");
  private panel = document.getElementById("text-properties")!;
  private gesture: {
    id: number;
    start: Point;
    current?: Point;
    node?: SceneNode;
    handle?: number;
    anchor?: number;
    placement?: "text" | "text-field";
    activation?: SceneNode;
  } | null = null;
  private range = { start: 0, end: 0 };
  private propertyPreview = false;
  private selectedKey = "";
  private generation = -1;
  private typing: Partial<CharacterStyle> | undefined;
  private alignment: "left" | "center" | "right" = "left";
  private fontPreview: SceneNode[] | null = null;
  private fontMenu: HTMLElement | null = null;
  private deformation: NativeTextDeformation;
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    private overlay: HTMLCanvasElement,
    private camera: Camera,
    private changed: () => void,
    private select: () => void,
    private blocked: () => boolean,
    private message: (text: string, error?: boolean) => void,
  ) {
    this.input.id = "native-text-input";
    this.input.setAttribute("aria-label", "Редактирование текста");
    this.input.spellcheck = false;
    this.input.autocomplete = "off";
    this.input.hidden = true;
    document.getElementById("native-editor")!.append(this.input);
    // iOS keyboard activation belongs to the native touchend, after Pointer
    // Events have completed placement. Cancel its compatibility/default tap so
    // the canvas cannot reclaim focus. Mouse and pen keep synchronous focus.
    overlay.addEventListener(
      "touchstart",
      (e) => {
        this.touchSequence = e.touches.length === 1;
        this.touchFocusPending = false;
      },
      { passive: true },
    );
    overlay.addEventListener(
      "touchend",
      (e) => {
        this.touchSequence = false;
        const focus = this.touchFocusPending;
        this.touchFocusPending = false;
        if (!focus || e.touches.length || !this.node || this.blocked()) return;
        e.preventDefault();
        this.positionInput(this.node);
        this.input.focus({ preventScroll: true });
        this.input.setSelectionRange(this.range.start, this.range.end);
      },
      { passive: false },
    );
    overlay.addEventListener(
      "touchcancel",
      () => {
        this.touchSequence = false;
        this.touchFocusPending = false;
      },
      { passive: true },
    );
    this.input.oninput = () => {
      const n = this.node;
      if (!n) return;
      const next = measureTextNode(
        replaceText(n, this.input.value, this.typing),
        n,
      );
      this.session.replaceNode(next);
      this.captureRange();
      if (this.input.value.trim() || !this.fresh) this.session.finish();
      this.changed();
    };
    for (const type of ["select", "keyup", "click"] as const)
      this.input.addEventListener(type, () => {
        this.captureRange();
        this.changed();
      });
    this.input.onkeydown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        this.finish();
        this.select();
      } else if (
        (e.ctrlKey || e.metaKey) &&
        !e.altKey &&
        ["KeyZ", "KeyY"].includes(e.code)
      ) {
        e.preventDefault();
        const id = this.id;
        this.session.undo(e.code === "KeyY" || e.shiftKey);
        if (!this.session.page.nodes.some((n) => n.id === id)) {
          this.finish();
        } else {
          this.input.value = textData(this.node!).value;
          this.range.end = this.range.start = Math.min(
            this.range.start,
            this.input.value.length,
          );
          this.input.setSelectionRange(this.range.start, this.range.end);
        }
        this.changed();
      } else if (
        (e.ctrlKey || e.metaKey) &&
        ["KeyB", "KeyI", "KeyU"].includes(e.code)
      ) {
        e.preventDefault();
        const key =
          e.code === "KeyB"
            ? "bold"
            : e.code === "KeyI"
              ? "italic"
              : "underline";
        this.applyStyle({ [key]: !this.style()[key] });
      }
    };
    this.panel.addEventListener("mousedown", (e) => {
      if ((e.target as HTMLElement).closest("button") && this.id) {
        e.preventDefault();
        this.captureRange();
      }
    });
    for (const [id, key] of [
      ["font-size", "size"],
      ["font-color", "color"],
    ] as const) {
      const field = document.getElementById(id) as HTMLInputElement;
      const apply = (commit: boolean) => {
        const value = key === "size" ? Number(field.value) : field.value;
        if (
          key === "size" &&
          (!Number.isFinite(value) || Number(value) < 1 || Number(value) > 200)
        )
          return;
        this.applyStyle(
          {
            ...(key === "color"
              ? textPaintPatch(field, "color")
              : { [key]: value }),
          },
          commit,
        );
      };
      field.oninput = () => apply(false);
      field.onchange = () => apply(true);
      field.onkeydown = (e) => {
        if (e.key === "Escape" && this.propertyPreview) {
          e.preventDefault();
          e.stopPropagation();
          this.session.cancel();
          this.propertyPreview = false;
          field.blur();
          this.changed();
        }
      };
    }
    const family = document.getElementById("font-family") as HTMLSelectElement;
    family.onchange = () => void this.loadFont(family.value, true);
    family.onpointerdown = (e) => {
      if (
        e.pointerType !== "touch" &&
        !matchMedia("(max-width:1100px)").matches
      ) {
        e.preventDefault();
        this.openFontMenu(family);
      }
    };
    for (const key of ["bold", "italic", "underline"] as const)
      document.getElementById("text-" + key)!.onclick = () =>
        this.applyStyle({ [key]: !this.style()[key] });
    for (const align of ["left", "center", "right"] as const)
      document.getElementById("align-" + align)!.onclick = () => {
        this.alignment = align;
        this.patch({ align });
        this.changed();
      };
    for (const [id, key, scale, min, max] of [
      ["text-line-spacing", "lineSpacing", 0.01, 50, 500],
      ["text-letter-spacing", "letterSpacing", 1, -50, 200],
      ["stroke-width", "strokeWidth", 1, 0, 10],
      ["stroke-color", "stroke", 1, 0, 0],
    ] as const) {
      const field = document.getElementById(id) as HTMLInputElement;
      const apply = (commit: boolean) => {
        const value =
          key === "stroke" ? field.value : Number(field.value) * scale;
        if (
          key !== "stroke" &&
          (!Number.isFinite(value) ||
            Number(field.value) < min ||
            Number(field.value) > max)
        )
          return;
        this.patch(
          {
            ...(key === "stroke"
              ? textPaintPatch(field, "stroke")
              : { [key]: value }),
          },
          commit,
        );
      };
      field.oninput = () => apply(false);
      field.onchange = () => apply(true);
      field.onkeydown = (e) => {
        if (e.key === "Escape" && this.propertyPreview) {
          e.preventDefault();
          e.stopPropagation();
          this.session.cancel();
          this.propertyPreview = false;
          this.changed();
        }
      };
    }
    overlay.addEventListener(
      "dblclick",
      (e) => {
        if (this.blocked()) return;
        const n = hitNode(
          session.page.nodes,
          documentPoint({ x: e.clientX, y: e.clientY }, camera),
          camera.zoom,
        );
        if (n?.kind === "text" && !n.layer.blockRole) {
          this.select();
          this.begin(n, false);
          e.preventDefault();
        }
      },
      true,
    );
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (!this.id || this.blocked()) return;
        const target = e.target as HTMLElement;
        if (
          !target.closest(
            "#irl-color-picker,#text-properties,#native-text-input,#native-text-compact,#native-text-deformation-compact,.native-font-menu,.native-zoom-control",
          ) &&
          target !== overlay
        ) {
          this.finish();
          this.changed();
        }
      },
      true,
    );
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (
          this.fontMenu &&
          !(e.target as HTMLElement).closest(".native-font-menu,#font-family")
        )
          this.closeFontMenu(false);
      },
      true,
    );
    wrapOriginalSection("text-properties", [
      {
        title: "Шрифт и начертание",
        icon: "font",
        selectors: [".font-controls", ".text-controls"],
        open: true,
      },
      {
        title: "Интервалы",
        icon: "line-spacing",
        selectors: [".text-spacing-controls"],
      },
      {
        title: "Обводка",
        icon: "stroke-width",
        selectors: [".text-stroke-controls"],
      },
    ]);
    originalCompact("native-text-compact", [
      { id: "font-family", icon: "font", title: "Шрифт" },
      { id: "font-size", icon: "font-size", title: "Размер шрифта" },
      { id: "font-color", icon: "fill", title: "Цвет текста" },
      {
        id: "text-line-spacing",
        icon: "line-spacing",
        title: "Межстрочный интервал",
      },
      {
        id: "text-letter-spacing",
        icon: "letter-spacing",
        title: "Межсимвольный интервал",
      },
      {
        icon: "align-center",
        title: "Выравнивание текста",
        exclusive: true,
        choices: [
          ["align-left", "align-left", "По левому краю"],
          ["align-center", "align-center", "По центру"],
          ["align-right", "align-right", "По правому краю"],
        ],
      },
      {
        icon: "bold",
        title: "Начертание текста",
        choices: [
          ["text-bold", "bold", "Жирный"],
          ["text-italic", "italic", "Курсив"],
          ["text-underline", "underline", "Подчёркивание"],
        ],
      },
      {
        id: "stroke-width",
        icon: "stroke-width",
        title: "Толщина обводки текста",
      },
      { id: "stroke-color", icon: "color", title: "Цвет обводки текста" },
    ]);
    this.deformation = new NativeTextDeformation(
      session,
      this.panel,
      changed,
      () => this.blocked() || this.previewing,
      () => this.closeFontMenu(false),
    );
  }
  get node() {
    return this.session.page.nodes.find(
      (n) => n.id === this.id && n.kind === "text",
    );
  }
  get previewing() {
    return !!this.fontPreview || this.propertyPreview;
  }
  get frameEditing() {
    return !!this.node && textData(this.node).frame;
  }
  get editing() {
    return !!this.id;
  }
  get active() {
    return !!this.gesture;
  }
  activate(tool: WorkTool | null) {
    this.finish();
    this.tool = tool === "text" || tool === "text-field" ? tool : null;
    this.changed();
  }
  private captureRange() {
    if (this.id) {
      this.range = {
        start: this.input.selectionStart,
        end: this.input.selectionEnd,
      };
    }
  }
  private style() {
    const n =
      this.node ?? this.session.selectedNodes.find((n) => n.kind === "text");
    return n
      ? characterStyle(textData(n), this.range.start)
      : {
          family: (document.getElementById("font-family") as HTMLSelectElement)
            .value,
          size: Number(
            (document.getElementById("font-size") as HTMLInputElement).value,
          ),
          color: (document.getElementById("font-color") as HTMLInputElement)
            .value,
          bold: false,
          italic: false,
          underline: false,
        };
  }
  private focusInput() {
    if (this.touchSequence) {
      this.touchFocusPending = true;
      return;
    }
    this.input.focus({ preventScroll: true });
  }
  private positionInput(node: SceneNode) {
    const m = nodeMatrix(node),
      v = window.visualViewport;
    const left = v?.offsetLeft ?? 0,
      top = v?.offsetTop ?? 0;
    // Position before focus too: stale/offscreen input coordinates can make
    // mobile browsers pan or zoom the page while opening the keyboard.
    this.input.style.left =
      Math.max(
        left,
        Math.min(
          left + (v?.width ?? innerWidth) - 2,
          m[4] * this.camera.zoom + this.camera.x,
        ),
      ) + "px";
    this.input.style.top =
      Math.max(
        top,
        Math.min(
          top + (v?.height ?? innerHeight) - 2,
          m[5] * this.camera.zoom + this.camera.y,
        ),
      ) + "px";
  }
  private begin(node: SceneNode, fresh: boolean) {
    this.finish();
    this.session.select([node.id]);
    this.id = node.id;
    this.fresh = fresh;
    this.input.value = textData(node).value;
    this.positionInput(node);
    this.input.hidden = false;
    this.focusInput();
    this.input.setSelectionRange(
      this.input.value.length,
      this.input.value.length,
    );
    this.captureRange();
    this.changed();
  }
  finish() {
    this.touchFocusPending = false;
    this.closeFontMenu(false);
    if (this.id) {
      const n = this.node;
      if (n && this.fresh && !textData(n).value.trim()) {
        this.session.replaceNodes(
          this.session.page.nodes.filter((n) => n.id !== this.id),
        );
      }
      this.session.finish();
    }
    this.id = null;
    this.gesture = null;
    this.input.hidden = true;
    this.typing = undefined;
    this.fresh = false;
    this.propertyPreview = false;
  }
  private patch(patch: Record<string, unknown>, commit = true) {
    this.closeFontMenu(false);
    const nodes = this.node
      ? [this.node]
      : this.session.selectedNodes.filter((n) => n.kind === "text");
    if (!nodes.length) return;
    if (!commit && !this.propertyPreview) {
      this.session.begin();
      this.propertyPreview = true;
    }
    this.session.replaceMany(
      nodes.map((n) =>
        // Alignment works inside the current layout; it must not resize or
        // move an existing label (including imported layouts).
        "align" in patch
          ? { ...n, text: { ...n.text, ...patch } }
          : measureTextNode({ ...n, text: { ...n.text, ...patch } }, n),
      ),
    );
    if (commit) {
      this.session.finish();
      this.propertyPreview = false;
    }
    this.refocus();
    this.changed();
  }
  private applyStyle(patch: Partial<CharacterStyle>, commit = true) {
    const nodes = this.node
      ? [this.node]
      : this.session.selectedNodes.filter((n) => n.kind === "text");
    if (!nodes.length) return;
    if (this.node && this.range.start === this.range.end) {
      this.typing = { ...this.style(), ...patch };
      if (textData(this.node).value.length) {
        this.refocus();
        this.changed();
        return;
      }
    }
    if (!commit && !this.propertyPreview) {
      this.session.begin();
      this.propertyPreview = true;
    }
    this.session.replaceMany(
      nodes.map((n) =>
        measureTextNode(
          styleText(
            n,
            patch,
            this.node ? this.range.start : 0,
            this.node ? this.range.end : textData(n).value.length,
          ),
          n,
        ),
      ),
    );
    if (commit) {
      this.session.finish();
      this.propertyPreview = false;
    }
    this.refocus();
    this.changed();
  }
  private refocus() {
    if (
      document.activeElement !== this.input &&
      document.activeElement?.matches("input,select,textarea")
    )
      return;
    if (this.id) {
      this.focusInput();
      this.input.setSelectionRange(this.range.start, this.range.end);
    }
  }
  private async loadFont(family: string, commit: boolean) {
    const token = ++this.loading,
      gen = this.session.generation,
      rev = this.session.revision,
      ids = this.session.selectedIds.join();
    try {
      await document.fonts.load(textFont({ ...this.style(), family }));
      if (
        token !== this.loading ||
        gen !== this.session.generation ||
        rev !== this.session.revision ||
        ids !== this.session.selectedIds.join()
      )
        return;
      if (commit) {
        this.closeFontMenu(false);
        this.applyStyle({ family });
      } else {
        if (!this.fontPreview)
          this.fontPreview = this.session.selectedNodes.filter(
            (n) => n.kind === "text",
          );
        this.session.replaceMany(
          this.fontPreview.map((n) =>
            measureTextNode(
              styleText(
                n,
                { family },
                this.node ? this.range.start : 0,
                this.node ? this.range.end : textData(n).value.length,
              ),
              n,
            ),
          ),
        );
        this.changed();
      }
    } catch (e) {
      this.message((e as Error).message, true);
    }
  }
  private openFontMenu(field: HTMLSelectElement) {
    if (this.fontMenu) {
      this.closeFontMenu(false);
      return;
    }
    this.captureRange();
    const menu = document.createElement("div");
    menu.className = "native-font-menu";
    menu.setAttribute("role", "listbox");
    for (const option of field.options) {
      const b = document.createElement("button");
      b.textContent = option.text;
      b.dataset.font = option.value;
      b.onpointerdown = (e) => e.preventDefault();
      b.onpointerenter = () => void this.loadFont(option.value, false);
      b.onclick = () => void this.loadFont(option.value, true);
      menu.append(b);
    }
    menu.onpointerleave = () => this.cancelFontPreview();
    menu.onkeydown = (e) => {
      const buttons = [...menu.querySelectorAll("button")],
        i = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (["ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        const b =
          buttons[
            (i + (e.key === "ArrowDown" ? 1 : -1) + buttons.length) %
              buttons.length
          ]!;
        b.focus();
        void this.loadFont(b.dataset.font!, false);
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        this.closeFontMenu(false);
        this.refocus();
      }
    };
    document.getElementById("native-editor")!.append(menu);
    const r = field.getBoundingClientRect();
    menu.style.left = Math.min(innerWidth - 260, r.left) + "px";
    menu.style.top = Math.min(innerHeight - 350, r.bottom) + "px";
    this.fontMenu = menu;
    menu
      .querySelector<HTMLButtonElement>(
        `[data-font="${CSS.escape((field as HTMLSelectElement).value)}"]`,
      )
      ?.focus();
  }
  cancelFontPreview() {
    this.loading++;
    if (this.fontPreview) {
      this.session.replaceMany(this.fontPreview);
      this.fontPreview = null;
      this.changed();
    }
  }
  private closeFontMenu(commit: boolean) {
    if (!commit) this.cancelFontPreview();
    this.fontMenu?.remove();
    this.fontMenu = null;
  }
  private caretAt(node: SceneNode, p: Point) {
    const inv = invertAffine(nodeMatrix(node))!,
      q = transformPoint(p, inv),
      t = textData(node),
      x = (q.x + 0.5) * t.layoutWidth,
      y = (q.y + 0.5) * t.layoutHeight,
      layout = textLayout(node);
    if (curvedText(t) || node.geometry.warpCorners) {
      const project = textProjector(node);
      let closest = 0,
        distance = Infinity;
      layout.carets.forEach((c, i) => {
        if (t.frame && (c.y < 0 || c.y >= t.layoutHeight)) return;
        const pose = textPose(t, c.x, c.y),
          a = project(pose),
          b = project(posePoint(pose, 0, c.height)),
          dx = b.x - a.x,
          dy = b.y - a.y,
          along = Math.max(
            0,
            Math.min(
              1,
              ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy || 1),
            ),
          ),
          d = Math.hypot(p.x - a.x - along * dx, p.y - a.y - along * dy);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      return closest;
    }
    let closest = 0,
      d = Infinity;
    layout.carets.forEach((c, i) => {
      const dy =
          y < c.y ? c.y - y : y > c.y + c.height ? y - c.y - c.height : 0,
        dist = Math.abs(c.x - x) + dy * 100;
      if (dist < d) {
        d = dist;
        closest = i;
      }
    });
    return closest;
  }
  event(e: PointerEvent) {
    if (
      this.blocked() ||
      this.pointer.space ||
      e.button === 1 ||
      ((e.ctrlKey || e.metaKey) && !this.gesture && !this.editing)
    )
      return false;
    const p = documentPoint({ x: e.clientX, y: e.clientY }, this.camera),
      n = this.node;
    if (e.type === "pointerdown" && e.button === 0) {
      if (n) {
        const handle = textData(n).frame
          ? hitFrameHandle(
              nodeMatrix(n),
              p,
              this.camera.zoom,
              e.pointerType === "touch",
            )
          : -1;
        if (handle >= 0) {
          this.session.begin();
          this.gesture = { id: e.pointerId, start: p, node: n, handle };
        } else if (hitNode([n], p)) {
          const anchor = e.shiftKey ? this.range.start : this.caretAt(n, p);
          this.range = {
            start: Math.min(anchor, this.caretAt(n, p)),
            end: Math.max(anchor, this.caretAt(n, p)),
          };
          this.refocus();
          this.gesture = { id: e.pointerId, start: p, node: n, anchor };
        } else {
          this.finish();
          this.tool = null;
          this.select();
          return false;
        }
      } else if (this.tool) {
        const existing = hitNode(this.session.page.nodes, p, this.camera.zoom);
        // Complete the initiating gesture before focusing the native input.
        // On touch, opening the keyboard on pointerdown lets the remaining
        // release/default activation take focus back from it.
        this.gesture =
          existing?.kind === "text" && !existing.layer.blockRole
            ? { id: e.pointerId, start: p, activation: existing }
            : { id: e.pointerId, start: p, current: p, placement: this.tool };
      } else return false;
      if (this.gesture) this.overlay.setPointerCapture(e.pointerId);
      this.changed();
      return true;
    }
    const g = this.gesture;
    if (!g || g.id !== e.pointerId) return false;
    if (e.type === "pointercancel") {
      this.touchFocusPending = false;
      if (g.node && g.handle !== undefined) this.session.cancel();
      this.gesture = null;
      this.changed();
      return true;
    }
    if (e.type === "pointermove" || e.type === "pointerup") {
      if (g.placement) g.current = p;
      if (g.node && g.handle !== undefined) {
        const t = textData(g.node),
          m = resizeMatrix(
            nodeMatrix(g.node),
            handles[g.handle]!,
            { x: p.x - g.start.x, y: p.y - g.start.y },
            true,
            e.altKey,
            { x: 60 / t.layoutWidth, y: 40 / t.layoutHeight },
          );
        try {
          const next = transformNativeNode(g.node, m);
          next.text = {
            ...t,
            layoutWidth:
              (t.layoutWidth * next.geometry.width!) / g.node.geometry.width!,
            layoutHeight:
              (t.layoutHeight * next.geometry.height!) /
              g.node.geometry.height!,
          };
          this.session.replaceNode(next);
        } catch {
          /* Keep last valid frame. */
        }
      } else if (g.node && g.anchor !== undefined) {
        const end = this.caretAt(g.node, p);
        this.range = {
          start: Math.min(g.anchor, end),
          end: Math.max(g.anchor, end),
        };
        this.refocus();
      }
    }
    if (e.type === "pointerup") {
      this.gesture = null;
      if (g.activation) {
        this.begin(g.activation, false);
      } else if (g.placement) {
        const frame = g.placement === "text-field";
        const b = frame
            ? drawBox(g.start, p, true, false)
            : { ...g.start, width: 0, height: 0 },
          node = createTextNode(
            crypto.randomUUID(),
            b.x,
            b.y,
            frame,
            Math.max(60, b.width || 240),
            Math.max(40, b.height || 80),
          );
        node.text = {
          ...node.text,
          ...this.style(),
          align: this.alignment,
          ...(frame ? { size: 28 } : {}),
        };
        if (!frame)
          node.transform.x =
            b.x +
            node.geometry.width! *
              (this.alignment === "left"
                ? 0.5
                : this.alignment === "right"
                  ? -0.5
                  : 0);
        this.session.begin();
        this.session.replaceNodes([...this.session.page.nodes, node]);
        this.begin(node, true);
      } else if (g.handle !== undefined) this.session.finish();
      if (this.overlay.hasPointerCapture(e.pointerId))
        this.overlay.releasePointerCapture(e.pointerId);
    }
    this.changed();
    return true;
  }
  cancelGesture() {
    this.touchFocusPending = false;
    const g = this.gesture;
    if (!g) return;
    if (g.node && g.handle !== undefined) this.session.cancel();
    this.gesture = null;
    if (this.overlay.hasPointerCapture(g.id))
      this.overlay.releasePointerCapture(g.id);
    this.changed();
  }
  key(e: KeyboardEvent) {
    if (e.key !== "Escape" || !this.gesture) return false;
    this.cancelGesture();
    return true;
  }
  render(ctx: CanvasRenderingContext2D) {
    if (this.generation !== this.session.generation) {
      this.generation = this.session.generation;
      if (this.id && !this.node) this.finish();
    }
    if (this.id && (!this.node || !this.session.selectedIds.includes(this.id)))
      this.finish();
    const n =
      this.node ?? this.session.selectedNodes.find((n) => n.kind === "text");
    const selectedKey = this.session.selectedIds.join();
    if (selectedKey !== this.selectedKey) {
      this.selectedKey = selectedKey;
      if (n)
        (this.panel.querySelector("details") as HTMLDetailsElement).open = true;
    }
    this.panel.hidden = !n && !this.tool;
    document.getElementById("native-text-compact")!.hidden = this.panel.hidden;
    if (n) {
      const t = textData(n),
        s = this.typing ?? characterStyle(t, this.id ? this.range.start : 0);
      this.alignment = t.align;
      for (const [id, value] of [
        ["font-family", s.family],
        ["font-size", s.size],
        ["font-color", s.color],
        ["text-line-spacing", t.lineSpacing * 100],
        ["text-letter-spacing", t.letterSpacing],
        ["stroke-width", t.strokeWidth],
        ["stroke-color", t.stroke],
      ] as const) {
        const field = document.getElementById(id) as HTMLInputElement;
        if (document.activeElement !== field) field.value = String(value);
        if (id === "font-color")
          field.dataset.displayAlpha = String(s.colorAlpha ?? 1);
        if (id === "stroke-color")
          field.dataset.displayAlpha = String(t.strokeAlpha ?? 1);
      }
      syncPaintInput(
        document.getElementById("font-color") as HTMLInputElement,
        s.colorGradient,
      );
      syncPaintInput(
        document.getElementById("stroke-color") as HTMLInputElement,
        t.strokeGradient,
      );
      for (const key of ["bold", "italic", "underline"] as const) {
        const button = document.getElementById("text-" + key)!;
        button.setAttribute("aria-pressed", String(s[key]));
        button.classList.toggle("active", !!s[key]);
      }
    }
    const alignments = this.node
      ? [textData(this.node).align]
      : this.session.selectedNodes
          .filter((n) => n.kind === "text")
          .map((n) => textData(n).align);
    if (!alignments.length) alignments.push(this.alignment);
    for (const align of ["left", "center", "right"] as const) {
      const button = document.getElementById("align-" + align)!;
      const active = alignments.every((value) => value === align);
      button.setAttribute(
        "aria-pressed",
        active ? "true" : alignments.includes(align) ? "mixed" : "false",
      );
      button.classList.toggle("active", active);
    }
    syncOriginalCompact("native-text-compact");
    this.deformation.render();
    const g = this.gesture;
    if (g?.placement === "text-field") {
      const b = drawBox(g.start, g.current ?? g.start, true, false);
      const width = Math.max(60, b.width),
        height = Math.max(40, b.height);
      drawTextFrame(
        ctx,
        [width, 0, 0, height, b.x + width / 2, b.y + height / 2],
        this.camera,
        false,
      );
    }
    if (!this.node) return;
    const node = this.node,
      t = textData(node),
      m = nodeMatrix(node),
      z = this.camera.zoom;
    this.positionInput(node);
    const layout = textLayout(node);
    if (curvedText(t) || node.geometry.warpCorners) {
      const project = textProjector(node),
        screen = (p: Point) => {
          const q = project(p);
          return { x: q.x * z + this.camera.x, y: q.y * z + this.camera.y };
        };
      ctx.save();
      ctx.fillStyle = "rgba(80,130,255,.3)";
      for (const g of layout.glyphs) {
        if (
          g.index < this.range.start ||
          g.index >= this.range.end ||
          (t.frame && g.y >= t.layoutHeight)
        )
          continue;
        const baseline =
            g.y +
            (t.metrics
              ? g.height / t.lineSpacing / t.metrics.lineHeight
              : g.style.size) *
              (t.metrics?.baseline ?? 0.9),
          pose = textPose(t, g.x + g.width / 2, baseline),
          top = Math.max(0, g.y) - baseline,
          bottom =
            (t.frame
              ? Math.min(t.layoutHeight, g.y + g.height)
              : g.y + g.height) - baseline;
        ctx.beginPath();
        [
          [-g.width / 2, top],
          [g.width / 2, top],
          [g.width / 2, bottom],
          [-g.width / 2, bottom],
        ].forEach(([x, y], i) => {
          const p = screen(posePoint(pose, x!, y!));
          if (i) ctx.lineTo(p.x, p.y);
          else ctx.moveTo(p.x, p.y);
        });
        ctx.closePath();
        ctx.fill();
      }
      if (this.range.start === this.range.end) {
        const c = layout.carets[this.range.start];
        if (c && (!t.frame || c.y < t.layoutHeight)) {
          const pose = textPose(t, c.x, c.y),
            a = screen(pose),
            b = screen(
              posePoint(
                pose,
                0,
                t.frame ? Math.min(c.height, t.layoutHeight - c.y) : c.height,
              ),
            );
          ctx.strokeStyle = "#192a42";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      ctx.restore();
      if (t.frame) drawTextFrame(ctx, m, this.camera, true);
      return;
    }
    ctx.save();
    ctx.translate(this.camera.x, this.camera.y);
    ctx.scale(z, z);
    ctx.transform(
      m[0] / t.layoutWidth,
      m[1] / t.layoutWidth,
      m[2] / t.layoutHeight,
      m[3] / t.layoutHeight,
      m[4],
      m[5],
    );
    ctx.translate(-t.layoutWidth / 2, -t.layoutHeight / 2);
    if (t.frame) {
      ctx.beginPath();
      ctx.rect(0, 0, t.layoutWidth, t.layoutHeight);
      ctx.clip();
    }
    ctx.fillStyle = "rgba(80,130,255,.3)";
    for (const glyph of layout.glyphs)
      if (glyph.index >= this.range.start && glyph.index < this.range.end)
        ctx.fillRect(glyph.x, glyph.y, glyph.width, glyph.height);
    if (this.range.start === this.range.end) {
      const c = layout.carets[this.range.start];
      if (c) {
        ctx.fillStyle = "#192a42";
        ctx.fillRect(c.x, c.y, 1 / z, c.height);
      }
    }
    ctx.restore();
    if (t.frame) drawTextFrame(ctx, m, this.camera, true);
  }
}
